import { useRef, useState } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'

const EnhancedMyWork = () => {
  const sectionRef = useRef(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const [expandedProject, setExpandedProject] = useState(null)

  const projects = [
    {
      id: 'backend-platform',
      filename: 'enterprise-platform.sys',
      title: 'Backend Platform — 14,000+ Users',
      subtitle: 'Multi-tenant system with security hardening & fine-grained access control',
      story: 'An organization needed a production backend serving thousands of users across multiple tenants with strict data isolation, security compliance, and zero tolerance for breaches.',
      solution: 'Architected a multi-tenant platform with JWT fingerprinting, rate limiting per API domain, CSRF protection, and fine-grained RBAC. Includes graceful shutdowns, failure fallbacks, and structured logging — because I\'m the one who gets the call at 2AM.',
      impact: '14,000+ active users. Multi-tenant isolation. Zero security breaches. 99.9% uptime.',
      tech: ['Node.js', 'MongoDB', 'JWT Fingerprinting', 'RBAC', 'Rate Limiting', 'CSRF'],
      metrics: [
        { key: 'users', value: '14k+', color: 'var(--accent)' },
        { key: 'breaches', value: '0', color: 'var(--syntax-green)' },
        { key: 'uptime', value: '99.9%', color: 'var(--syntax-amber)' }
      ]
    },
    {
      id: 'bi-system',
      filename: 'business-intel.pipe',
      title: 'Business Intelligence System',
      subtitle: 'Aggregating 12+ data sources across 50+ retail branches',
      story: 'Executives needed real-time visibility into sales, delivery operations, productivity metrics, and customer satisfaction across 50+ retail branches. Data was scattered across 12+ sources with no unified view.',
      solution: 'Built a BI system that aggregates data from 12+ sources into automated executive reports. Covers sales performance, delivery ops, productivity metrics, and customer satisfaction. Competition and incentive systems that drive real operational behavior across distributed teams.',
      impact: 'Automated executive reporting across 50+ branches. Incentive systems driving real behavior change in distributed teams.',
      tech: ['Node.js', 'Python', 'Data Aggregation', 'ETL', 'Automated Reporting'],
      metrics: [
        { key: 'sources', value: '12+', color: 'var(--accent)' },
        { key: 'branches', value: '50+', color: 'var(--syntax-green)' },
        { key: 'reports', value: 'Auto', color: 'var(--syntax-purple)' }
      ]
    },
    {
      id: 'saversonic',
      filename: 'saversonic.app',
      title: 'SaverSonic',
      subtitle: 'Free file conversion & dev tools platform',
      link: 'https://saversonic.com',
      story: 'People constantly need to convert files, compress images, format JSON, or decode JWTs — but every tool out there is either bloated with ads, requires sign-up, or charges subscription fees.',
      solution: 'Built a full platform with 30+ tools across 6 categories: PDF, spreadsheet, video/audio, image, text, and developer tools. No accounts, no ads, files auto-delete after 30 minutes. Built with Next.js and server-side processing for speed.',
      impact: 'Free, fast, private. Drop files, get results. Used by developers and professionals who need quick conversions without the bloat.',
      tech: ['Next.js', 'React', 'Tailwind CSS', 'Server-side Processing', 'File APIs'],
      metrics: [
        { key: 'tools', value: '30+', color: 'var(--accent)' },
        { key: 'categories', value: '6', color: 'var(--syntax-green)' },
        { key: 'cost', value: 'Free', color: 'var(--syntax-amber)' }
      ]
    },
    {
      id: 'lavetach',
      filename: 'lavetach-ai.app',
      title: 'Lavetach AI',
      subtitle: 'AI-powered case management for mortgage brokers',
      link: 'https://ai.lavetach.com',
      story: 'Mortgage brokers drown in admin — emails, PDFs, WhatsApp messages, call recordings. All needs to be matched to clients and turned into tasks. Manually, it eats hours every day.',
      solution: 'Built a full AI platform that reads emails, PDFs, WhatsApp, and call recordings. AI extracts tasks and matches them to clients automatically. BYOK model so data never touches my servers. Role-based access control for teams.',
      impact: 'Brokers review and approve AI suggestions instead of doing manual data entry. Hours of admin work reduced to minutes.',
      tech: ['React', 'Node.js', 'AI/LLM', 'WhatsApp API', 'Gmail API', 'RBAC'],
      metrics: [
        { key: 'channels', value: '4', color: 'var(--accent)' },
        { key: 'privacy', value: 'BYOK', color: 'var(--syntax-green)' },
        { key: 'status', value: 'Live', color: 'var(--syntax-amber)' }
      ]
    },
    {
      id: 'excel-mcp',
      filename: 'excel-mcp.oss',
      title: 'Excel MCP Server',
      subtitle: 'Open-source MCP server for Claude — 37 tools, 25+ stars',
      link: 'https://github.com/ishayoyo/excel-mcp',
      story: 'Claude had no way to directly work with spreadsheets. Developers needed to manually copy-paste data or write custom scripts every time they wanted AI to analyze Excel files.',
      solution: 'Built an MCP server with 37 tools — read, write, analyze, clean, and model financial data in Excel/CSV. Streams 100MB+ files without loading into memory. Supports natural language formula parsing and AI-powered analysis.',
      impact: '25+ GitHub stars. Used with Claude Code, Claude Desktop, and Cursor. MIT licensed. TypeScript, zero-config install via npx.',
      tech: ['TypeScript', 'MCP Protocol', 'Node.js', 'Excel/CSV', 'AI Integration'],
      metrics: [
        { key: 'tools', value: '37', color: 'var(--accent)' },
        { key: 'stars', value: '25+', color: 'var(--syntax-green)' },
        { key: 'files', value: '100MB+', color: 'var(--syntax-purple)' }
      ]
    },
    {
      id: 'porthub',
      filename: 'porthub.oss',
      title: 'PortHub',
      subtitle: 'Open-source CLI tool & npm package — see what\'s using your ports, kill it',
      link: 'https://github.com/ishayoyo/porthub',
      npmLink: 'https://www.npmjs.com/package/porthub',
      story: 'Every developer knows the pain: "port 3000 is already in use." Finding what process is hogging a port means remembering different OS commands, parsing messy output, and manually killing processes. Existing tools are bloated with dependencies.',
      solution: 'Built a zero-config CLI that shows port, PID, process name, uptime, and memory in a clean table. Interactive kill prompts, multi-port scanning, cross-platform (macOS, Linux, Windows). Just 1 dependency. Works instantly via npx — no install needed.',
      impact: 'Published on npm as "porthub". Cross-platform, MIT licensed. Replaces tools with 11+ dependencies using just 1. Used by developers who need quick, clean port management.',
      tech: ['Node.js', 'CLI', 'npm Package', 'Cross-Platform', 'Open Source'],
      metrics: [
        { key: 'deps', value: '1', color: 'var(--accent)' },
        { key: 'platforms', value: '3', color: 'var(--syntax-green)' },
        { key: 'license', value: 'MIT', color: 'var(--syntax-purple)' }
      ]
    },
    {
      id: 'magen-carmeli',
      filename: 'magen-carmeli.platform',
      title: 'Magen Carmeli',
      subtitle: 'Support platform for military families',
      link: 'https://magencarmeli.org.il',
      story: 'Families of the Carmeli military division needed a centralized platform for support, resources, and community connection. Nothing existed.',
      solution: 'Built and deployed a complete community platform — support resources, family assistance coordination, and communication hub. Designed for real people with real needs.',
      impact: 'Live platform serving families of the Carmeli division. Deployed and maintained in production.',
      tech: ['React', 'Node.js', 'Full-Stack', 'Community Platform'],
      metrics: [
        { key: 'type', value: 'NGO', color: 'var(--accent)' },
        { key: 'users', value: 'Families', color: 'var(--syntax-green)' },
        { key: 'status', value: 'Live', color: 'var(--syntax-amber)' }
      ]
    },
    {
      id: 'magen-mluim',
      filename: 'magen-mluim.platform',
      title: 'Magen Mluim',
      subtitle: 'Support system for reserve families',
      link: 'https://magenmiluim.org',
      story: 'Reserve duty families face unique challenges — a dedicated support system to connect, share resources, and get assistance during service periods.',
      solution: 'Built a full community platform tailored to reserve families. End-to-end: design, development, deployment, and ongoing maintenance.',
      impact: 'Complete system built and deployed for a real community need. Another production system I own front to back.',
      tech: ['React', 'Node.js', 'Full-Stack', 'Community Platform'],
      metrics: [
        { key: 'type', value: 'NGO', color: 'var(--accent)' },
        { key: 'scope', value: 'End-to-end', color: 'var(--syntax-green)' },
        { key: 'status', value: 'Live', color: 'var(--syntax-amber)' }
      ]
    }
  ]

  return (
    <section ref={sectionRef} className="py-16 sm:py-24" style={{ background: 'var(--bg-deep)' }}>
      <div className="container mx-auto px-4 sm:px-6">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-10 sm:mb-16"
        >
          <div className="font-mono text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
            <span style={{ color: 'var(--syntax-green)' }}>// </span>
            ls -la ./projects
          </div>
          <h2 className="font-mono text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            My <span style={{ color: 'var(--accent)' }}>Work</span>
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }} className="text-lg max-w-2xl">
            Real systems, in production, built end-to-end.
            Click any project to dig deeper.
          </p>
        </motion.div>

        {/* Project cards */}
        <div className="space-y-6 max-w-5xl">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.15 * index }}
              className="terminal-card cursor-pointer"
              onClick={() => setExpandedProject(expandedProject === project.id ? null : project.id)}
            >
              {/* Terminal header bar */}
              <div className="terminal-header">
                <div className="terminal-dot" style={{ background: '#FF5F57' }} />
                <div className="terminal-dot" style={{ background: '#FEBC2E' }} />
                <div className="terminal-dot" style={{ background: '#28C840' }} />
                <span className="ml-3">{project.filename}</span>
                <span className="ml-auto text-[#555570]">
                  {expandedProject === project.id ? '[-]' : '[+]'}
                </span>
              </div>

              {/* Card content */}
              <div className="p-4 sm:p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 sm:gap-6">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-mono text-base sm:text-xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>
                      {project.title}
                    </h3>
                    <p className="font-mono text-sm mb-4" style={{ color: 'var(--accent)' }}>
                      {project.subtitle}
                    </p>

                    {/* Tech tags */}
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                      {project.tech.map((tech) => (
                        <span
                          key={tech}
                          className="font-mono text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded border"
                          style={{
                            color: 'var(--text-secondary)',
                            borderColor: 'var(--border)',
                            background: 'var(--bg-elevated)'
                          }}
                        >
                          {tech}
                        </span>
                      ))}
                      {project.link && (
                        <a
                          href={project.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="font-mono text-[10px] sm:text-xs px-2 sm:px-3 py-0.5 rounded border transition-all duration-300 hover:shadow-[0_0_10px_rgba(0,255,224,0.15)]"
                          style={{
                            color: 'var(--accent)',
                            borderColor: 'var(--accent)',
                            background: 'rgba(0,255,224,0.05)'
                          }}
                        >
                          {project.link.includes('github.com') ? 'github' : 'visit'} &rarr;
                        </a>
                      )}
                      {project.npmLink && (
                        <a
                          href={project.npmLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="font-mono text-[10px] sm:text-xs px-2 sm:px-3 py-0.5 rounded border transition-all duration-300 hover:shadow-[0_0_10px_rgba(248,113,113,0.15)]"
                          style={{
                            color: 'var(--syntax-red)',
                            borderColor: 'var(--syntax-red)',
                            background: 'rgba(248,113,113,0.05)'
                          }}
                        >
                          npm &rarr;
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Metrics */}
                  <div className="flex gap-4 sm:gap-6 lg:gap-8">
                    {project.metrics.map((metric) => (
                      <div key={metric.key} className="text-center">
                        <div className="font-mono text-lg sm:text-2xl font-bold" style={{ color: metric.color }}>
                          {metric.value}
                        </div>
                        <div className="font-mono text-[10px] sm:text-xs" style={{ color: 'var(--text-muted)' }}>
                          {metric.key}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Expanded details */}
                <AnimatePresence>
                  {expandedProject === project.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-4 sm:mt-6 pt-4 sm:pt-6" style={{ borderTop: '1px solid var(--border)' }}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                          <div>
                            <h4 className="font-mono text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--syntax-amber)' }}>
                              The Challenge
                            </h4>
                            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }}>
                              {project.story}
                            </p>
                          </div>
                          <div>
                            <h4 className="font-mono text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--syntax-blue)' }}>
                              My Solution
                            </h4>
                            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }}>
                              {project.solution}
                            </p>
                          </div>
                          <div>
                            <h4 className="font-mono text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--syntax-green)' }}>
                              The Impact
                            </h4>
                            <p className="text-sm leading-relaxed" style={{ color: 'var(--syntax-green)', fontFamily: 'var(--font-body)' }}>
                              {project.impact}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  )
}

export default EnhancedMyWork
